import { AgenturNameCell } from 'app/components/shared/AgenturName.cell';
import { Table } from 'app/components/table/table';
import { AggregrationType, ITableColumn, TableModel } from 'app/components/table/table.model';
import { useStore } from 'app/context';
import { observer } from 'mobx-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { WkzgKofferVM } from 'app/stores/ui/agt.wkzgkoffer.ui.store';
import { ProgressRingSmall } from 'app/components/common/ProgressRing';
import { countArray, formatPercentWithIcon } from 'app/utils';
import { init, EChartsOption, ECharts } from 'echarts';
import { debounce } from 'lodash';
import colors from 'assets/scss/colors.module.scss';

interface IWerkzeugProgress {
	numLeft?: number;
	total?: number;
	color?: string;
}
export const WerkzeugProgress = (props: IWerkzeugProgress) => {
	const { numLeft, total, color } = props;
	if (numLeft === undefined || !total) return <></>;

	const num = total - numLeft;
	const perc = Math.floor((num / total) * 100);

	return (
		<>
			<div className="grid is-col-auto justify-start align-center">
				<ProgressRingSmall progress={perc} color={color} />
				<div>
					<span className="is-bold">{perc}%</span>{' '}
					<span className="color-grey">
						({num}/{total})
					</span>
				</div>
			</div>
		</>
	);
};

interface IWerkzeugkofferBarChart {
	data: WkzgKofferVM[];
	color?: string;
	year: number;
}

const WerkzeugkofferBarChart = observer((props: IWerkzeugkofferBarChart) => {
	const { uiStore } = useStore();
	const { data, color } = props;

	const chartRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!data || data.length === 0) return;

		let categories = [
			{ label: 'Wertgut 30 EUR', total: 'wertgut30Eur', numLeft: 'restWertgut' },
			{ label: 'Downgrade', total: 'downgrade', numLeft: 'restDowngrade' },
			{ label: 'SB Verzicht', total: 'sbverzicht', numLeft: 'restSbverz' },
			{ label: 'Nachhaltigkeitsbonus', total: 'nachhaltigkeitsbonus', numLeft: 'restNachhbo' },
			{ label: 'Preisgarantie', total: 'preisgarantie', numLeft: 'restPreisg' },
			{ label: 'BAK-Verzicht', total: 'bakVerzicht', numLeft: 'restBakVerz' },
			{ label: 'Joker', total: 'joker', numLeft: 'jokerRest' },
			{ label: 'Kontingent', total: 'sumKontingent', numLeft: 'sumRest' },
		].reverse();

		if (props.year < 2026) {
			categories = categories.filter((x) => x.label !== 'Joker' && x.label !== 'BAK-Verzicht');
		}

		const chartData = categories.map((cat) => {
			const total = countArray(data.map((vm) => (vm.bak as any)[cat.total] || 0));
			const numLeft = countArray(data.map((vm) => (vm.bak as any)[cat.numLeft] || 0));
			const num = total - numLeft;
			const percentage = total > 0 ? Math.floor((num / total) * 100) : 0;

			return {
				category: cat.label,
				percentage,
			};
		});

		const option: EChartsOption = {
			tooltip: {
				trigger: 'axis',
				formatter: '{b}: {c}%',
			},
			xAxis: {
				type: 'value',
				max: 100,
				axisLabel: {
					formatter: '{value}%',
					fontSize: 12,
				},
			},
			yAxis: {
				type: 'category',
				data: chartData.map((d) => d.category),
				axisLabel: {
					fontSize: 12,
				},
			},
			series: [
				{
					type: 'bar',
					data: chartData.map((d) => d.percentage),
					itemStyle: {
						color: color || colors.primary,
					},
					label: {
						show: true,
						position: 'right',
						formatter: '{c}%',
						fontSize: 11,
					},
				},
			],
			grid: {
				left: '150px',
				right: '100px',
				top: '10px',
				bottom: '20px',
			},
			animation: !uiStore.printMode,
		};

		let chart: ECharts | null = null;
		if (chartRef.current) {
			chart = init(chartRef.current, null, { renderer: 'svg' });
			chart.setOption(option);
		}

		const handleResize = debounce(() => {
			chart?.resize();
		}, 300);
		window.addEventListener('resize', handleResize);

		return () => {
			chart?.dispose();
			window.removeEventListener('resize', handleResize);
		};
	}, [data, uiStore.printMode]);

	return <div ref={chartRef} style={{ width: '100%', height: '300px', marginBottom: '2rem' }} />;
});

interface IWerkzeugkofferBakListe {
	year: number;
}

export const WerkzeugkofferBakListe = observer((props: IWerkzeugkofferBakListe) => {
	const { berichteUiiStore, agtWkzkofferUiStore, agenturListUiStore } = useStore();
	const [hasItems, setHasItems] = useState(false);
	const [chartData, setChartData] = useState<WkzgKofferVM[]>([]);

	const tm = useMemo(() => {
		const tm = new TableModel<WkzgKofferVM, number>();
		const cols: ITableColumn<WkzgKofferVM, number>[] = [];

		cols.push({
			label: 'Agentur',
			path: 'agt.name',
			sortBy: 'agt.name',
			render: AgenturNameCell,
		});

		cols.push({
			label: 'Nachtrag AfA',
			path: 'bak.nachtragsliste',
			agg: {
				aggType: AggregrationType.count,
				aggIf: (val: string) => {
					return val === 'Ja';
				},
			},
		});
		cols.push({
			label: 'Wertgut 30 EUR',
			path: 'bak.restWertgut',
			render(row) {
				return <WerkzeugProgress numLeft={row.data.bak.restWertgut} total={row.data.bak.wertgut30Eur} color={colors.turquoise} />;
			},
			agg: {
				aggType: AggregrationType.sum,
				render(tm) {
					const num = countArray(tm.data.map((vm) => vm.bak.restWertgut));
					const total = countArray(tm.data.map((vm) => vm.bak.wertgut30Eur));
					return <WerkzeugProgress numLeft={num} total={total} color={colors.turquoise} />;
				},
			},
		});
		cols.push({
			label: 'Downgrade',
			path: 'bak.restDowngrade',
			render(row) {
				return <WerkzeugProgress numLeft={row.data.bak.restDowngrade} total={row.data.bak.downgrade} color={colors.turquoise} />;
			},
			agg: {
				aggType: AggregrationType.sum,
				render(tm) {
					const num = countArray(tm.data.map((vm) => vm.bak.restDowngrade));
					const total = countArray(tm.data.map((vm) => vm.bak.downgrade));
					return <WerkzeugProgress numLeft={num} total={total} color={colors.turquoise} />;
				},
			},
		});

		cols.push({
			label: 'SB Verzicht',
			path: 'bak.restSbverz',
			render(row) {
				return <WerkzeugProgress numLeft={row.data.bak.restSbverz} total={row.data.bak.sbverzicht} color={colors.turquoise} />;
			},
			agg: {
				aggType: AggregrationType.sum,
				render(tm) {
					const num = countArray(tm.data.map((vm) => vm.bak.restSbverz));
					const total = countArray(tm.data.map((vm) => vm.bak.sbverzicht));
					return <WerkzeugProgress numLeft={num} total={total} color={colors.turquoise} />;
				},
			},
		});

		cols.push({
			label: 'Nachhaltigkeitsbonus',
			path: 'bak.restNachhbo',
			render(row) {
				return <WerkzeugProgress numLeft={row.data.bak.restNachhbo} total={row.data.bak.nachhaltigkeitsbonus} color={colors.turquoise} />;
			},
			agg: {
				aggType: AggregrationType.sum,
				render(tm) {
					const num = countArray(tm.data.map((vm) => vm.bak.restNachhbo));
					const total = countArray(tm.data.map((vm) => vm.bak.nachhaltigkeitsbonus));
					return <WerkzeugProgress numLeft={num} total={total} color={colors.turquoise} />;
				},
			},
		});

		cols.push({
			label: 'Preisgarantie',
			path: 'bak.restPreisg',
			render(row) {
				return <WerkzeugProgress numLeft={row.data.bak.restPreisg} total={row.data.bak.preisgarantie} color={colors.turquoise} />;
			},
			agg: {
				aggType: AggregrationType.sum,
				render(tm) {
					const num = countArray(tm.data.map((vm) => vm.bak.restPreisg));
					const total = countArray(tm.data.map((vm) => vm.bak.preisgarantie));
					return <WerkzeugProgress numLeft={num} total={total} color={colors.turquoise} />;
				},
			},
		});

		if (props.year === 2026) {
			cols.push({
				label: 'BAK-Verzicht',
				path: 'bak.restBakVerz',
				render(row) {
					return <WerkzeugProgress numLeft={row.data.bak.restBakVerz} total={row.data.bak.bakVerzicht} color={colors.turquoise} />;
				},
				agg: {
					aggType: AggregrationType.sum,
					render(tm) {
						const num = countArray(tm.data.map((vm) => vm.bak.restBakVerz));
						const total = countArray(tm.data.map((vm) => vm.bak.bakVerzicht));
						return <WerkzeugProgress numLeft={num} total={total} color={colors.turquoise} />;
					},
				},
			});
			cols.push({
				label: 'Joker',
				path: 'bak.joker',
				render(row) {
					return <WerkzeugProgress numLeft={row.data.bak.jokerRest} total={row.data.bak.joker} color={colors.turquoise} />;
				},

				agg: {
					aggType: AggregrationType.sum,
					render(tm) {
						const num = countArray(tm.data.map((vm) => vm.bak.jokerRest));
						const total = countArray(tm.data.map((vm) => vm.bak.joker));
						return <WerkzeugProgress numLeft={num} total={total} color={colors.turquoise} />;
					},
				},
			});
		}

		cols.push({
			label: 'Kontingent',
			path: 'bak.sumRest',
			render(row) {
				return <WerkzeugProgress numLeft={row.data.bak.sumRest} total={row.data.bak.sumKontingent} color={colors.turquoise} />;
			},
			agg: {
				aggType: AggregrationType.sum,
				render(tm) {
					const num = countArray(tm.data.map((vm) => vm.bak.sumRest));
					const total = countArray(tm.data.map((vm) => vm.bak.sumKontingent));
					return <WerkzeugProgress numLeft={num} total={total} color={colors.turquoise} />;
				},
			},
		});

		cols.push({
			label: 'Gesamt-Durchschnitt',
			path: 'bak.bakAvg',
			format: formatPercentWithIcon,
			agg: {
				aggType: AggregrationType.avg,
				format: formatPercentWithIcon,
			},
		});

		// TEMP debug columns to analyze inconsistent sum_kontingent in the 2026 delivery - remove later
		// (requires the commented-out debug fields in BakModel, agt.wkzgkoffer.ui.store.ts)
		// const debugErrStyle: React.CSSProperties = {
		// 	background: '#cc0f35',
		// 	color: '#fff',
		// 	fontFamily: 'monospace',
		// 	fontWeight: 'bold',
		// 	padding: '2px 6px',
		// 	whiteSpace: 'nowrap',
		// };
		// cols.push({
		// 	label: 'DEBUG: SK (Lieferung)',
		// 	path: 'bak.sumKontingentDelivered',
		// 	render(row) {
		// 		const bak = row.data.bak;
		// 		if (bak.skIsInconsistent) {
		// 			return <span style={debugErrStyle}>{bak.sumKontingentDelivered} error</span>;
		// 		}
		// 		return <>{bak.sumKontingentDelivered}</>;
		// 	},
		// });
		// cols.push({
		// 	label: 'DEBUG: Σ Kontingent (berechnet)',
		// 	path: 'bak.sumKontingent',
		// });
		// cols.push({
		// 	label: 'DEBUG: Σ Rest (berechnet)',
		// 	path: 'bak.sumRest',
		// });

		tm.sortBy = 'agt.name';
		tm.setCols(cols);

		tm.idProperty = 'agtId';
		tm.idType = 'number';

		return tm;
	}, []);

	useEffect(() => {
		agtWkzkofferUiStore.load().then(() => {
			let items = agtWkzkofferUiStore.items.filter((x) => x.bak.year === props.year);

			// filter only agt with bak
			items = items.filter((i) => i.bak.hasWkzg);
			if (berichteUiiStore.selectedAgt) {
				const agtId = berichteUiiStore.selectedAgt.agtId;
				items = items.filter((i) => i.agt.agtId === agtId);
			} else {
				items = agenturListUiStore.applyFilterOnAgt(items) as WkzgKofferVM[];
			}
			setHasItems(items.length > 0);
			setChartData(items);
			tm.setRowData(items);
			tm.sort();
		});
	}, [tm, berichteUiiStore.selectedAgt, agenturListUiStore.filterHasChanged, agenturListUiStore, agtWkzkofferUiStore.items, agtWkzkofferUiStore]);

	return (
		<>
			<div className="table-container pad-bottom-3rem">
				{hasItems && chartData.length > 0 && (
					<div className="pad-bottom-2rem">
						<h3 className="pad-1rem is-centered is-bold">BAK Werkzeugkoffer</h3>
						<WerkzeugkofferBarChart data={chartData} color={colors.turquoise} year={props.year} />
					</div>
				)}
				{!hasItems ? (
					<div className="pad-1rem">Keine Werkzeugkoffer Teilnahme</div>
				) : (
					<>
						<Table stickyHeader={true} tm={tm}></Table>
					</>
				)}
			</div>
		</>
	);
});
